import ErrorHandler, { getErrorOptions } from 'common/error/components/error-handler';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';
import styled from 'styled-components';
import { Div, notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import CreateOrderSidepanel from '../containers/create-order-sidepanel-container';
import * as styles from './create-order.css';
import NavContextItem from './nav-context-item';
import NewOrderForm from './new-order-form';
import OrdersTable from './orders-table';
import Question from './question';

const choices = [
  { id: 'yes', translationKey: 'orderWizard.ANSWER_YES' },
  { id: 'no', translationKey: 'orderWizard.ANSWER_NO' },
];

const formName = 'newOrderForm';

const Title = styled.div`
  display: flex;
  width: 100%;
`;

class CreateOrder extends Component {
  componentDidMount() {
    const { initializeOrder, id, match } = this.props;
    initializeOrder({ id, type: match.params.type, ticketActionId: match.params.actionId });
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.redirectUrl && nextProps.redirectUrl !== this.props.redirectUrl) {
      notification.success({
        message: this.props.intl.formatMessage({
          id: 'orderWizard.MSG_SUCCESS',
        }),
        placement: 'top',
      });
      nextProps.next(nextProps.redirectUrl.split('/')[1]);
    }
    if (nextProps.id !== this.props.id) {
      nextProps.initializeOrder({
        id: nextProps.id,
        type: nextProps.match.params.type,
        ticketActionId: nextProps.match.params.actionId,
      });
    }
  }

  getIndicator = (values) => {
    const { indicators, indicatorTypes, useIndicatorWizard } = this.props;
    if (values.splitInvoice) {
      let indicator = indicators?.find(t => t.id === values.indicator);
      if (useIndicatorWizard) {
        indicator = indicatorTypes?.find(t => t.id === values.indicatorType);
        return indicator?.splitInvoiceIndicator?.indicator?.id;
      }
      return indicator?.splitInvoiceIndicator?.id;
    }
    return useIndicatorWizard ? values.indicatorType : values.indicator;
  };

  handleNewOrderSubmit = (values) => {
    const indicator = this.getIndicator(values);
    const mappedValues = {
      ...values,
      indicator,
      description: values.description_custom || values.description || values.indicatorDescription,
      orderType: values.orderType || values.indicatorOrderType,
      paidBy: values.paidBy || values.indicatorPaidBy,
      ownDescription: !!values.description_custom,
    };

    // Delete all _custom keys to keep the call clean
    const filteredMappedValues = Object.keys(mappedValues).reduce((object, key) => {
      if (!key.match(/_custom/)) {
        object[key] = mappedValues[key];
      }
      return object;
    }, {});
    this.props.createOrder({
      ...filteredMappedValues,
      id: this.props.id,
      type: this.props.match?.params?.type,
      ticketActionId: this.props.match && this.props.match.params.actionId,
    });
  };

  handleSelectedScheduleSubmit = scheduledId =>
    this.props.createOrder({
      scheduledId,
      id: this.props.id,
      type: this.props.match?.params?.type,
      ticketActionId: this.props.match && this.props.match.params.actionId,
    });

  handleSelectionChange = (e) => {
    const { change, orderTypeHasValues, setDefaults } = this.props;

    change(formName, e.target.name, e.target.value);
    change(formName, 'splitInvoice', 0);
    // Logic for clearing some dropdowns when a specific dropdown changes
    switch (e.target.name) {
      case 'orderType':
        change(formName, 'description', '');
        change(formName, 'paidBy', '');
        change(formName, 'paidByValue', '');
        change(formName, 'indicator', '');
        change(formName, 'businessSubType', null);
        setDefaults();
        if (orderTypeHasValues) {
          change(formName, 'value', '');
        }
        break;
      case 'description':
        change(formName, 'description_custom', '');
        setDefaults();
        break;
      case 'paidBy':
        change(formName, 'paidByValue', '');
        change(formName, 'indicator', '');
        change(formName, 'businessSubType', null);
        if (orderTypeHasValues) {
          change(formName, 'value', '');
        }
        setDefaults();
        break;
      case 'paidByValue':
        change(formName, 'businessSubType', null);
        if (orderTypeHasValues) {
          change(formName, 'value', '');
        }
        setDefaults();
        break;
      case 'indicator':
        change(formName, 'businessSubType', null);
        if (orderTypeHasValues) {
          change(formName, 'value', '');
        }
        setDefaults();
        break;
      default:
        break;
    }
  };

  handleIndicatorSelectionChange = (e) => {
    const { change, setIndicatorDefaults, indicatorInputs } = this.props;

    change(formName, e.target.name, e.target.value);

    // Logic for clearing some dropdowns when a specific dropdown changes
    if (e.target.name === 'indicatorType') {
      if (indicatorInputs.length) {
        indicatorInputs.forEach((input) => {
          change(formName, input.name, '');
        });
      }
      change(formName, 'splitInvoice', 0);
      change(formName, 'description_custom', '');
      change(formName, 'businessSubType', null);
      setIndicatorDefaults();
    }
  };

  render() {
    const {
      isLoading,
      answer,
      machineIsRunning,
      orderTypes,
      descriptions,
      costPaidBys,
      paidByValues,
      indicators,
      dynamicValue = {},
      navContext,
      scheduledItems,
      intl,
      isSubmitting,
      error,
      goToDashboard,
      progressLabel,
      description,
      wizardInputs,
      orderType,
      indicatorTypes,
      indicatorInputs,
      shouldRenderIndicatorDescription,
      useIndicatorWizard,
      toggleIndicatorWizard,
      setDefaults,
      setIndicatorDefaults,
      splitInvoiceIndicator,
      splitInvoice,
      directSelectionOnly,
      wizardType,
      businessSubTypes,
      indicatorBusinessSubTypes,
      resetError,
      isFiscalCodeServiceCenter
    } = this.props;

    // if we perform a bad request (the order cannot be initialized), close the modal.
    // but first, show the error message manually, as the errorHandler will also be gone.
    if (error && error.status && error.status === 400) {
      const options = getErrorOptions(error);
      notification.error({
        ...options,
        placement: 'top',
        onClose: () => {
          if (options?.onClose) {
            options.onClose();
          }
          resetError();
        },
      });
      goToDashboard();
      return null;
    }

    // NEW SIMPLIFIED INDICATOR LOGIC (not for PTS)
    if ((!directSelectionOnly && isToggleEnabled('simpleWizard'))
      || wizardType === 'functionallocation') {
      return <CreateOrderSidepanel
        progressLabel={progressLabel}
        goToDashboard={goToDashboard}
        handleSelectedScheduleSubmit={this.handleSelectedScheduleSubmit}
        id={this.props.id}
        ticketActionId={this.props.match?.params?.actionId}
        type={this.props.match?.params?.type}
      />;
    }

    return (
      <SidePanel
        title={
          <Title>
            <Div flex={1}>{intl.formatMessage({ id: 'orderWizard.TITLE' })}</Div>
            {progressLabel}
          </Title>
        }
        width='60%'
        onPanelClose={goToDashboard}
      >
        <ErrorHandler type='notification' error={error} />
        {isLoading ?
          <Spinner title={intl.formatMessage({ id: 'orderWizard.MSG_LOADING' })} />
          :
          <div className={styles.createOrder}>
            <section className={styles.navContext}>
              {navContext?.map(item =>
                <NavContextItem key={item.type} item={item} />,
              )}
            </section>
            <div className={styles.divider} />
            <section
              data-test-id='choice-machine-still-operational'
            >
              <Question choices={choices} value={machineIsRunning} onChange={answer} />
              <div className={styles.divider} />
            </section>
            <section className={!machineIsRunning && styles.hide}>
              <h3 className={styles.sectionHeader}>
                <FormattedMessage id='orderWizard.USE_SCHEDULED_ITEM' />
              </h3>
              <OrdersTable
                orders={scheduledItems}
                onSubmit={this.handleSelectedScheduleSubmit}
                isSubmitting={isSubmitting}
              />
              <div className={styles.divider} />
            </section>
            <section className={!machineIsRunning && styles.hide}>
              <NewOrderForm
                onSelectionChange={this.handleSelectionChange}
                onIndicatorSelectionChange={this.handleIndicatorSelectionChange}
                onSubmit={this.handleNewOrderSubmit}
                setDefaults={setDefaults}
                setIndicatorDefaults={setIndicatorDefaults}
                isSubmitting={isSubmitting}
                toggleIndicatorWizard={toggleIndicatorWizard}
                useIndicatorWizard={useIndicatorWizard}
                directSelectionOnly={directSelectionOnly}
                data={{
                  orderTypes,
                  descriptions,
                  costPaidBys,
                  paidByValues,
                  indicators,
                  dynamicValue,
                  description,
                  wizardInputs,
                  orderType,
                  indicatorTypes,
                  indicatorInputs,
                  shouldRenderIndicatorDescription,
                  machineIsRunning,
                  splitInvoiceIndicator,
                  splitInvoice,
                  businessSubTypes,
                  indicatorBusinessSubTypes,
                  isFiscalCodeServiceCenter
                }}
              />
            </section>
          </div>
        }
      </SidePanel>
    );
  }
}

CreateOrder.propTypes = {
  answer: PropTypes.func.isRequired,
  initializeOrder: PropTypes.func.isRequired,
  machineIsRunning: PropTypes.string,
  orderTypes: PropTypes.array,
  descriptions: PropTypes.array,
  costPaidBys: PropTypes.array,
  paidByValues: PropTypes.shape({ items: PropTypes.array }),
  indicators: PropTypes.array,
  dynamicValue: PropTypes.shape({ items: PropTypes.array }),
  navContext: PropTypes.array,
  scheduledItems: PropTypes.array,
  isLoading: PropTypes.bool,
  createOrder: PropTypes.func,
  intl: PropTypes.object,
  change: PropTypes.func,
  setDefaults: PropTypes.func,
  setIndicatorDefaults: PropTypes.func,
  orderTypeHasValues: PropTypes.bool,
  redirectUrl: PropTypes.string,
  error: PropTypes.object,
  isSubmitting: PropTypes.bool,
  id: PropTypes.string,
  next: PropTypes.func,
  goToDashboard: PropTypes.func,
  progressLabel: PropTypes.node,
  description: PropTypes.object,
  orderType: PropTypes.object,
  wizardInputs: PropTypes.object,
  indicatorInputs: PropTypes.array,
  indicatorTypes: PropTypes.array,
  businessSubTypes: PropTypes.array,
  indicatorBusinessSubTypes: PropTypes.array,
  shouldRenderIndicatorDescription: PropTypes.bool,
  toggleIndicatorWizard: PropTypes.func,
  useIndicatorWizard: PropTypes.bool,
  match: PropTypes.match,
  splitInvoiceIndicator: PropTypes.object,
  splitInvoice: PropTypes.object,
  directSelectionOnly: PropTypes.bool,
  wizardType: PropTypes.string,
  resetError: PropTypes.func,
  isFiscalCodeServiceCenter: PropTypes.booln
};

export default compose(injectIntl)(withRouter(CreateOrder));
